import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { clearPreferences, CONSENT_EVENT, CONSENT_KEY, isAnalyticsEnabled, readConsent, saveConsent } from '../../lib/consent';

const ConsentContext = createContext(null);
export const useConsent = () => useContext(ConsentContext);

export default function ConsentProvider({ children }) {
  const [{ consent }, setConsentState] = useState(() => ({ consent: readConsent() }));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const opener = useRef(null);
  const openSettings = () => {
    opener.current = document.activeElement;
    setSettingsOpen(true);
  };
  const restoreFocus = () => {
    const target = opener.current?.isConnected ? opener.current : document.querySelector('[data-testid="consent-settings"]');
    target?.focus({ preventScroll: true });
  };

  useEffect(() => {
    let timer;
    const refresh = () => {
      clearTimeout(timer);
      const current = readConsent();
      // Refresh even when consent stays null: a failed write changes the runtime default.
      setConsentState({ consent: current });
      if (!current?.preferences) clearPreferences();
      if (current) timer = setTimeout(refresh, Math.min(current.expiresAt - Date.now() + 1, 86400000));
    };
    const onStorage = (event) => {
      if (event.key === CONSENT_KEY || event.key === null) refresh();
    };
    refresh();
    window.addEventListener(CONSENT_EVENT, refresh);
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', refresh);
    return () => {
      clearTimeout(timer);
      window.removeEventListener(CONSENT_EVENT, refresh);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  const choose = (preferences, analytics = false) => {
    if (!saveConsent(preferences, analytics)) return false;
    setSettingsOpen(false);
    return true;
  };

  return <ConsentContext.Provider value={{ consent, preferencesAllowed: consent?.preferences === true, analyticsAllowed: isAnalyticsEnabled(consent), settingsOpen, setSettingsOpen, openSettings, restoreFocus, choose }}>
    {children}
  </ConsentContext.Provider>;
}
